import { Controller } from "@hotwired/stimulus"

// Generic bottom sheet controller.
// Targets:
//   sheet   — the sliding panel
//   overlay — the dark backdrop
// Values:
//   desktopNoop (Boolean, default false) — see below
// Actions:
//   click->bottom-sheet#open   — open trigger
//   click->bottom-sheet#close  — dismiss trigger
//
// Drag-to-dismiss is wired automatically on the sheet in connect().
//
// desktopNoop opts an instance into no-opping entirely (connect(), open(),
// close() all return immediately) above the `md` (768px) breakpoint. This
// is for elements that carry this controller alongside a second,
// desktop-only controller managing an entirely different layout at that
// width -- e.g. the docked activity feed pairs this with collapsible-panel
// on the same element, and gating out here means the two never fight over
// the same target's inline styles. It is opt-in (data-bottom-sheet-desktop-
// noop-value="true"), NOT the default: every other bottom-sheet usage in
// the field/chat apps is a standalone instance with no competing
// desktop-oriented controller, and those must keep working regardless of
// viewport width -- both because a real user could genuinely open the app
// in a wide browser window, and because our system tests exercise these
// pages at a desktop-sized default viewport (the field app is mobile-only
// in production, but nothing resizes the test browser to match).
//
// When desktopNoop is on, the breakpoint is also watched live: if the
// viewport grows past it while the sheet is open, it is force-closed so
// nothing is left stuck mid-animation or covering the now-desktop layout.
export default class extends Controller {
  static targets = ["sheet", "overlay"]
  static values  = { desktopNoop: { type: Boolean, default: false } }

  connect() {
    if (this.desktopNoopValue) {
      this.mediaQuery = window.matchMedia("(min-width: 768px)")
      this._onViewportChange = () => {
        if (this.isDesktop()) {
          this._clearForDesktop()
        } else {
          this._initHidden()
        }
      }
      this.mediaQuery.addEventListener("change", this._onViewportChange)

      if (this.isDesktop()) return
    }

    this._initHidden()
    this._bindDrag()
  }

  disconnect() {
    if (this.mediaQuery) this.mediaQuery.removeEventListener("change", this._onViewportChange)
    document.body.style.overflow = ""
    this._unbindDrag()
  }

  open() {
    if (this.isDesktop()) return

    this._transitionsOn()
    this.overlayTarget.style.pointerEvents = "auto"
    this.overlayTarget.style.opacity       = "1"
    this.sheetTarget.style.transform       = "translateY(0)"
    document.body.style.overflow           = "hidden"
  }

  close() {
    if (this.isDesktop()) return
    this._reset()
  }

  // Only ever true for a desktopNoop instance above the breakpoint -- other
  // instances have no mediaQuery at all, so they always report false and
  // behave exactly as they did before desktopNoop existed.
  isDesktop() {
    return this.desktopNoopValue && this.mediaQuery.matches
  }

  // Closed/hidden starting state -- sheet off-screen, overlay inert.
  _initHidden() {
    this.sheetTarget.style.transform         = "translateY(100%)"
    this.overlayTarget.style.opacity         = "0"
    this.overlayTarget.style.pointerEvents   = "none"
    this.overlayTarget.style.backgroundColor = "rgba(0,0,0,0.6)"

    // Attach transitions one frame later so the initial hide doesn't animate
    requestAnimationFrame(() => {
      this._transitionsOn()
    })
  }

  // Closes the sheet unconditionally -- used by the public close() action
  // (once it's confirmed we're not desktop-no-opped) and by the
  // viewport-change listener, which must be able to force a close
  // regardless of breakpoint.
  _reset() {
    this._transitionsOn()
    this.overlayTarget.style.opacity       = "0"
    this.overlayTarget.style.pointerEvents = "none"
    this.sheetTarget.style.transform       = "translateY(100%)"
    document.body.style.overflow           = ""
  }

  // Crossing into desktop width -- clears every inline style this controller
  // may have applied (rather than re-setting a mobile-hide value), so the
  // `md:static` Tailwind class fully governs the panel's position in the
  // sidebar flex layout with zero residue from the mobile sheet state.
  _clearForDesktop() {
    this.sheetTarget.style.transform       = ""
    this.sheetTarget.style.transition      = ""
    this.overlayTarget.style.opacity       = ""
    this.overlayTarget.style.pointerEvents = ""
    this.overlayTarget.style.transition    = ""
    document.body.style.overflow           = ""
  }

  // ── Drag-to-dismiss ────────────────────────────────────────────────

  _bindDrag() {
    this._onTouchStart = this._touchStart.bind(this)
    this._onTouchMove  = this._touchMove.bind(this)
    this._onTouchEnd   = this._touchEnd.bind(this)

    this.sheetTarget.addEventListener("touchstart", this._onTouchStart, { passive: true })
    this.sheetTarget.addEventListener("touchmove",  this._onTouchMove,  { passive: false })
    this.sheetTarget.addEventListener("touchend",   this._onTouchEnd,   { passive: true })
  }

  _unbindDrag() {
    this.sheetTarget.removeEventListener("touchstart", this._onTouchStart)
    this.sheetTarget.removeEventListener("touchmove",  this._onTouchMove)
    this.sheetTarget.removeEventListener("touchend",   this._onTouchEnd)
  }

  _touchStart(e) {
    this._dragStartY    = e.touches[0].clientY
    this._dragLastY     = e.touches[0].clientY
    this._dragLastTime  = Date.now()
    this._dragVelocity  = 0
    this._dragging      = true
    this._transitionsOff()
  }

  _touchMove(e) {
    if (!this._dragging) return

    const y     = e.touches[0].clientY
    const delta = y - this._dragStartY

    // Only allow dragging downward; add rubber-band resistance for upward drags
    const clamped = delta > 0 ? delta : delta * 0.1

    // Track velocity (px/ms)
    const now      = Date.now()
    const elapsed  = now - this._dragLastTime || 1
    this._dragVelocity = (y - this._dragLastY) / elapsed
    this._dragLastY    = y
    this._dragLastTime = now

    this.sheetTarget.style.transform = `translateY(${clamped}px)`

    // Fade overlay proportionally to how far down the sheet is dragged
    const sheetHeight = this.sheetTarget.offsetHeight || 400
    const progress    = Math.min(Math.max(clamped / sheetHeight, 0), 1)
    this.overlayTarget.style.opacity = String(1 - progress)

    // Prevent page scroll while dragging the sheet
    if (delta > 0) e.preventDefault()
  }

  _touchEnd() {
    if (!this._dragging) return
    this._dragging = false

    const sheetHeight  = this.sheetTarget.offsetHeight || 400
    const currentY     = this._currentTranslateY()
    const pastThreshold = currentY > sheetHeight * 0.4
    const fastFlick    = this._dragVelocity > 0.5   // px/ms

    this._transitionsOn()

    if (pastThreshold || fastFlick) {
      this._reset()
    } else {
      // Spring back
      this.sheetTarget.style.transform = "translateY(0)"
      this.overlayTarget.style.opacity = "1"
    }
  }

  _currentTranslateY() {
    const matrix = new DOMMatrixReadOnly(getComputedStyle(this.sheetTarget).transform)
    return matrix.m42  // translateY component
  }

  _transitionsOn() {
    this.sheetTarget.style.transition   = "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)"
    this.overlayTarget.style.transition = "opacity 0.25s ease"
  }

  _transitionsOff() {
    this.sheetTarget.style.transition   = "none"
    this.overlayTarget.style.transition = "none"
  }
}
